<template>
  <section class="list-activity">
    <div class="list-activity-loading" v-if="isLoading">
      <InsightsLoading class="mt-4"/>
    </div>
    <div v-if="hasError" class="text-center pt-8 text-red-700">Couldn't load list activity</div>
    <template v-else>
      <p class="my-2">
        Find the “why” in a trend with list-level activity. Click on any time period in graph or select a time period
        below.
      </p>
      <slot/>
      <table class="list-activity-totals-table">
        <thead>
        <tr>
          <th></th>
          <th class="total font-bold">Up Votes</th>
          <th class="total font-bold">Down Votes</th>
          <th class="total font-bold">Total Votes</th>
          <th class="total font-bold">Sentiment</th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <th class="align-bottom text-left font-bold">Totals</th>
          <template v-if="listActivityTotals">
            <td>{{ formatNumbers(listActivityTotals.totalUpVotes) }}</td>
            <td>{{ formatNumbers(listActivityTotals.totalDownVotes) }}</td>
            <td>{{ formatNumbers(listActivityTotals.totalVotes) }}</td>
            <td>{{ listActivityTotals.sentiment }}%</td>
          </template>
        </tr>
        </tbody>
      </table>
      <hr class="my-2">
      <table class="list-activity-table" v-if="listActivity && listActivity.length">
        <thead>
        <tr class="font-bold">
          <th class="text-left cursor-pointer select-none" @click="sortBy('listName')">List Name
            <SortIcon name="listName" :current="sortByKey" :type="sortDirection"/>
          </th>
          <th class="cursor-pointer select-none" @click="sortBy('node.rank')">Rank
            <SortIcon name="node.rank" :current="sortByKey" :type="sortDirection"/>
          </th>
          <th class="cursor-pointer select-none" @click="sortBy('node.upVotes')">Up Votes
            <SortIcon name="node.upVotes" :current="sortByKey" :type="sortDirection"/>
          </th>
          <th class="cursor-pointer select-none" @click="sortBy('node.downVotes')">Down Votes
            <SortIcon name="node.downVotes" :current="sortByKey" :type="sortDirection"/>
          </th>
          <th class="cursor-pointer select-none" @click="sortBy('node.totalVotes')">Total Votes
            <SortIcon name="node.totalVotes" :current="sortByKey" :type="sortDirection"/>
          </th>
          <th class="cursor-pointer select-none" @click="sortBy('node.sentiment')">Sentiment
            <SortIcon name="node.sentiment" :current="sortByKey" :type="sortDirection"/>
          </th>
        </tr>
        </thead>
        <tbody>
          <tr v-for="(list, i) in sortedItems" :key="list.id" :class="{'gray-bg': i % 2}">
            <td class="text-left font-medium"><a :href="getUrl(list.url)" target="_blank">{{ list.listName }}</a></td>
            <td>{{ list.node.rank }} / {{ list.listItemsCount }}</td>
            <td>{{ formatNumbers(list.node.upVotes) }}</td>
            <td>{{ formatNumbers(list.node.downVotes) }}</td>
            <td>{{ formatNumbers(list.node.totalVotes) }}</td>
            <td>{{ list.node.sentiment !== null ? `${list.node.sentiment.toFixed(1)}%` : '-' }}</td>
          </tr>
        </tbody>
      </table>
      <div class="flex items-center justify-center w-full pb-4">
        <LoadMoreButton v-if="!isLoading && totalLists && totalLists > listActivity.length" @click="$emit('loadMore')">
          load all {{ totalLists }}
        </LoadMoreButton>
      </div>
    </template>
  </section>
</template>

<script>
import LoadMoreButton from '@/components/ui/buttons/LoadMoreButton';
import SortIcon from '@/components/ui/SortIcon';
import sortBy from 'lodash/sortBy';
import {formatNumbers} from '@/lib/utils';

export default {
  name: 'ListActivity',
  components: {SortIcon, LoadMoreButton},
  props: {
    listActivity: {
      type: Array
    },
    listActivityTotals: {
      type: Object
    },
    totalLists: {
      type: Number,
      default: 0
    },
    isLoading: {
      type: Boolean,
      default: true
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sortByKey: 'node.totalVotes',
      sortDirection: 'desc'
    };
  },
  computed: {
    sortedItems() {
      if (this.sortDirection === null) {
        return this.listActivity;
      }
      const results = sortBy(this.listActivity, [this.sortByKey]);
      if (this.sortDirection === 'desc') {
        results.reverse();
      }
      return results;
    }
  },
  methods: {
    formatNumbers,
    getUrl(nodeUrl) {
      return `${process.env.RANKER_DOMAIN}${nodeUrl}`;
    },
    sortBy(key) {
      if (this.sortByKey === key) {
        this.sortDirection = this.sortDirection === 'desc' ? 'asc' :
            this.sortDirection === 'asc' ? null : 'desc';
      }
      else {
        this.sortDirection = 'desc';
      }
      this.sortByKey = key;
    }
  }
};
</script>

<style lang="postcss">
.list-activity {
  @apply relative;

  .list-activity-loading {
    @apply absolute top-0 left-0 bottom-0 right-0 bg-white bg-opacity-80 z-10 pointer-events-none;
  }

  .list-activity-totals-table {
    @apply text-center w-full mb-2;
    font-size: 17px;

    .total {
      @apply w-1/6 pb-4;
    }
  }

  .list-activity-table {
    @apply text-center w-full mb-4 mt-10;

    th {
      border-bottom: 2px solid #f0f0f0;
      padding: 8px 0;
      font-size: 17px;
    }

    .gray-bg {
      background-color: #f0f0f0;
    }

    td {
      padding: 10px 0;
      font-size: 15px;
    }
  }
}
</style>